<script setup>
// Origem planilha. CSV é lido aqui mesmo; XLSX sobe para o backend Go, que já sabe lê-lo
// (`internal/importer`) e devolve as linhas puladas com motivo. `.xls` legado NÃO entra: o
// backend o recusa com "salve como .xlsx ou .csv", e oferecê-lo no seletor seria convidar o
// operador a escolher um arquivo que nunca vai passar — medido contra o Go em 21/09/2026. Parsear planilha no
// navegador obrigaria a uma biblioteca no bundle e a uma segunda implementação das mesmas
// regras de coluna — a decisão foi não ter a segunda cópia, não deixar de aceitar o formato.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/ui/Button.vue';
import { contactsFromCsv } from '../helpers/outboundContacts';

const emit = defineEmits(['loaded', 'upload']);

const { t } = useI18n();

// Literal keys per branch: a key built at runtime cannot be checked by the i18n linter,
// so a typo would only surface as a raw key on screen.
function csvErrorMessage(reason) {
  if (reason === 'MISSING_COLUMNS') {
    return t('OUTBOUNDS.SOURCE.CSV_ERROR.MISSING_COLUMNS');
  }
  if (reason === 'NO_VALID_ROWS') {
    return t('OUTBOUNDS.SOURCE.CSV_ERROR.NO_VALID_ROWS');
  }
  if (reason === 'UNSUPPORTED') {
    return t('OUTBOUNDS.SOURCE.CSV_ERROR.UNSUPPORTED');
  }
  return t('OUTBOUNDS.SOURCE.CSV_ERROR.EMPTY');
}

const fileInput = ref(null);
const fileName = ref('');
const error = ref('');
const count = ref(0);
const skipped = ref(0);

function reset() {
  fileName.value = '';
  error.value = '';
  count.value = 0;
  skipped.value = 0;
  if (fileInput.value) fileInput.value.value = '';
  emit('loaded', { contacts: [], label: '' });
}

async function handleFileChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  fileName.value = file.name;
  error.value = '';
  count.value = 0;
  skipped.value = 0;

  // Planilha vai inteira para o servidor: quem sabe ler .xlsx é o Go.
  if (/\.xlsx$/i.test(file.name)) {
    emit('upload', { file, label: file.name });
    return;
  }

  // O seletor filtra por extensão, mas um .xls renomeado ainda chega aqui como binário.
  if (/\.xls$/i.test(file.name)) {
    error.value = csvErrorMessage('UNSUPPORTED');
    emit('loaded', { contacts: [], label: '' });
    return;
  }

  const {
    contacts,
    skipped: dropped,
    error: reason,
  } = contactsFromCsv(await file.text());

  if (reason) {
    error.value = csvErrorMessage(reason);
    emit('loaded', { contacts: [], label: '' });
    return;
  }

  count.value = contacts.length;
  skipped.value = dropped;
  emit('loaded', { contacts, label: file.name });
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.SOURCE.CSV_HINT') }}
    </p>

    <input
      ref="fileInput"
      type="file"
      accept=".csv,.xlsx,text/csv"
      class="hidden"
      @change="handleFileChange"
    />

    <div class="flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" @click="fileInput?.click()">
        <i class="i-lucide-upload size-4 mr-1" />
        {{ t('OUTBOUNDS.SOURCE.CSV_PICK') }}
      </Button>

      <span v-if="fileName" class="text-sm text-n-slate-11">{{
        fileName
      }}</span>

      <Button v-if="fileName" variant="ghost" size="sm" @click="reset">
        {{ t('OUTBOUNDS.SOURCE.CSV_CLEAR') }}
      </Button>
    </div>

    <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>

    <template v-else-if="count">
      <p class="text-sm text-n-teal-11">
        {{ t('OUTBOUNDS.SOURCE.CSV_LOADED', { count }) }}
      </p>
      <p v-if="skipped" class="text-sm text-n-amber-11">
        {{ t('OUTBOUNDS.SOURCE.CSV_SKIPPED', { count: skipped }) }}
      </p>
    </template>
  </div>
</template>
